import { NEWS, RESOURCES, PARTNERS, IMAGES } from "../lib/content";
import { Link } from "react-router-dom";
import { FileDown, ArrowRight } from "lucide-react";

function Header({ overline, title, subtitle }) {
  return (
    <section className="bg-[#FDFBF7] border-b border-black/10">
      <div className="container-x py-20 md:py-24">
        <span className="overline text-[#6B21A8]">{overline}</span>
        <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-6 max-w-2xl text-black/70 text-lg">{subtitle}</p>}
      </div>
    </section>
  );
}

export function News() {
  return (
    <>
      <Header overline="News & Stories" title="From the brotherhood." subtitle="Weekly updates, announcements, and stories from our young men, mentors, and partners." />
      <section className="bg-white">
        <div className="container-x py-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {NEWS.map((n) => (
            <article key={n.slug} className="card-editorial p-8" data-testid={`news-${n.slug}`}>
              <p className="overline text-[#6B21A8]">{n.category}</p>
              <h2 className="mt-4 font-display text-2xl font-semibold leading-tight">{n.title}</h2>
              <p className="mt-3 text-sm text-black/70 leading-relaxed">{n.excerpt}</p>
              <p className="mt-6 text-xs uppercase tracking-widest text-black/50">{new Date(n.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export function Gallery() {
  const images = [
    { src: "/photos/grand-opening.jpg", caption: "Grand Opening · Change Center Mentoring HQ" },
    { src: "/photos/huddle-trust.jpg", caption: "Trust exercise · Young Men Ages 11–16" },
    { src: "/photos/prayer-huddle.jpg", caption: "Prayer & brotherhood" },
    { src: "/photos/change-tshirts.jpg", caption: "Mentors & mentees on service day" },
    { src: "/photos/classroom-cohort.jpg", caption: "Weekly cohort · school partnership" },
    { src: "/photos/outdoor-group.jpg", caption: "Founder with the young men outside HQ" },
    { src: "/photos/school-lineup.jpg", caption: "The next cohort · line up" },
    { src: "/photos/group-hug.jpg", caption: "End-of-session celebration" },
    { src: "/photos/founder-table.jpg", caption: "Recruiting the next cohort" },
    { src: "/photos/outdoor-huddle.jpg", caption: "Team huddle · outdoor session" },
  ];
  return (
    <>
      <Header overline="Photo Gallery" title="Moments that made us." subtitle="Retreats, service days, graduations, and everyday brotherhood." />
      <section className="bg-white">
        <div className="container-x py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
            {images.map((img, i) => (
              <figure key={i} className="relative overflow-hidden border border-black/10 aspect-square group">
                <img src={img.src} alt={img.caption} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.caption}
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-8 text-sm text-black/50">More albums coming soon — check back for event recaps and portraits.</p>
        </div>
      </section>
    </>
  );
}

export function Resources() {
  return (
    <>
      <Header overline="Resources" title="Everything in one place." subtitle="Handbooks, homework help, scholarships, career prep, and emergency resources for Clayton County families." />
      <section className="bg-white">
        <div className="container-x py-16 grid gap-6 md:grid-cols-2">
          {RESOURCES.map((r) => (
            <div key={r.title} className="border border-black/10 p-6 hover:shadow-[4px_4px_0_#0A0A0A] transition-shadow" data-testid={`resource-${r.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="overline text-[#6B21A8]">{r.tag}</p>
              <div className="mt-3 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl font-semibold">{r.title}</h3>
                  <p className="mt-2 text-sm text-black/70">{r.desc}</p>
                </div>
                <FileDown className="h-6 w-6 text-[#6B21A8] shrink-0" />
              </div>
              <a href="#" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6B21A8] hover:gap-3 transition-all">Download PDF <ArrowRight className="h-4 w-4" /></a>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function Partners() {
  const grouped = PARTNERS.reduce((acc, p) => { (acc[p.type] ??= []).push(p); return acc; }, {});
  return (
    <>
      <Header overline="Partners" title="Better together." subtitle="Schools, churches, businesses, and government agencies walking with us." />
      <section className="bg-white">
        <div className="container-x py-16 space-y-14">
          {Object.entries(grouped).map(([type, list]) => (
            <div key={type}>
              <p className="overline text-[#6B21A8] mb-6">{type}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {list.map((p) => (
                  <div key={p.name} className="border-l-2 border-[#F5B90A] pl-4 py-2">
                    <p className="font-display text-lg font-semibold">{p.name}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#F5B90A]">
        <div className="container-x py-20 grid gap-8 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight text-black">Ready to be part of this?</h2>
            <p className="mt-3 text-black/80 text-lg">Schools, churches, and businesses — let's talk.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-primary" data-testid="partners-contact-cta">Become a partner</Link>
          </div>
        </div>
      </section>
    </>
  );
}
